/**
 * ArcGIS `findAddressCandidates` result -> the `MblsAddress` shape the legacy
 * webapp's `/client/submitWithResponse` consumes.
 *
 * LOCKER-410: every field read here used to be assumed present. ArcGIS returns
 * `outFields: "*"`, but a candidate outside North America carries a `Country`
 * this module has no mapping for, and a POI / postal-centroid candidate can omit
 * `AddNum`, `LongLabel` or `ShortLabel` entirely. Both produced an uncaught
 * `TypeError` inside the `.map` in `useArcgisFindAddressCandidates`, which killed
 * the whole create-client dialog rather than one field of it.
 *
 * The rule settled on the ticket (`N1`): this function NEVER throws — an
 * unresolvable country yields an empty country component — and the *rejection*
 * is a separate, explicit decision made by the caller via
 * `isCandidateCountrySupported`. Detecting a bad candidate and crashing into one
 * are different things, and only the first can be shown to the user.
 */

interface AddressCandidateAttributes {
  AddNum?: string
  LongLabel?: string
  ShortLabel?: string
  City?: string
  Region?: string
  RegionAbbr?: string
  Country?: string
  Postal?: string
  PostalExt?: string
  District?: string
  X?: number
  Y?: number
  DisplayX?: number
  DisplayY?: number
}

interface AddressCandidate {
  address?: string
  location?: {
    x: number
    y: number
  }
  score?: number
  attributes: AddressCandidateAttributes
}

export interface MblsAddress {
  id: number
  address_components: Array<{
    long_name: string
    short_name: string
    types: string[]
  }>
  formatted_address: string
  geometry: {
    location: {
      lat: number
      lng: number
    }
  }
}

/**
 * The only countries the downstream legacy `Client` save path understands. A
 * candidate outside this set cannot produce a usable address component, which is
 * why `isCandidateCountrySupported` exists rather than a silent empty string.
 */
const countries: { [key: string]: { short: string; long: string } } = {
  CAN: { short: "CA", long: "Canada" },
  USA: { short: "US", long: "United States" },
}

/** A missing/non-string attribute reads as the empty string, never as `undefined`. */
const str = (value: unknown): string => (typeof value === "string" ? value : "")

/** A missing/non-numeric coordinate reads as 0 rather than `NaN`. */
const num = (value: unknown): number =>
  typeof value === "number" && Number.isFinite(value) ? value : 0

/**
 * `candidate.attributes`, or an empty object when the candidate is malformed.
 * Both public entry points funnel through here so neither can dereference
 * `undefined`.
 */
const attrsOf = (candidate: unknown): AddressCandidateAttributes =>
  candidate && typeof candidate === "object"
    ? (((candidate as AddressCandidate).attributes ||
        {}) as AddressCandidateAttributes)
    : {}

/**
 * The `{ short, long }` pair for a candidate's country, or `undefined` when the
 * candidate carries no `Country` or one outside `{CAN, USA}`.
 */
export const resolveCandidateCountry = (
  candidate: unknown,
): { short: string; long: string } | undefined => {
  const code = str(attrsOf(candidate).Country)
  // `countries[code]` alone resolves inherited keys — `Country: "constructor"`
  // would hand back `Object.prototype.constructor` and read as a supported
  // country. An own-property check is the only lookup that means what it says.
  return Object.prototype.hasOwnProperty.call(countries, code)
    ? countries[code]
    : undefined
}

/**
 * Whether this candidate's country can be represented downstream.
 *
 * The caller (`OsCreateMblsClientDialog`) uses this to surface a validation
 * error on the address field and block submit — `LOCKER-410 N1`/`DoD11`. Kept
 * separate from `convertToMblsAddress` on purpose: conversion must stay total,
 * so that one bad candidate in a list cannot take the dialog down with it.
 */
export const isCandidateCountrySupported = (candidate: unknown): boolean =>
  resolveCandidateCountry(candidate) !== undefined

/**
 * Whether the candidate carries a usable position.
 *
 * `num()` maps a missing or non-finite coordinate to `0`, which keeps
 * conversion total but puts the client at null island in the Gulf of Guinea —
 * a coordinate that looks perfectly valid to routing, distance and the map.
 * "Silently plausible" is a worse failure than "absent" for a coordinate, so
 * the caller checks this alongside the country and refuses the candidate
 * rather than shipping a real-looking lie (round 1, F1.4).
 */
export const hasUsableLocation = (candidate: unknown): boolean => {
  const attributes = attrsOf(candidate)
  const finite = (v: unknown) => typeof v === "number" && Number.isFinite(v)

  // `X`/`Y` are what the conversion reads by default, so they are required
  // outright: an absent `X` becomes 0 rather than triggering the branch below.
  if (!finite(attributes.X) || !finite(attributes.Y)) return false

  // This MIRRORS the ESRI workaround at the bottom of this file rather than
  // merely resembling it: past ±85 the conversion *replaces* the location with
  // the Display pair, so that pair — not `X`/`Y` — is what has to exist.
  //
  // The first cut treated the two pairs as interchangeable, which is wrong for
  // most of Canada and the US: a Winnipeg candidate (`X: -97.14`) with no
  // Display pair passed the guard and was still emitted at 0,0 (round 2, F2.1).
  const x = attributes.X as number
  if (x > 85 || x < -85) {
    return finite(attributes.DisplayX) && finite(attributes.DisplayY)
  }
  return true
}

/**
 * US postal codes are a single field; Canadian ones are split across
 * `Postal` + `PostalExt`. Missing parts collapse away instead of stringifying
 * `undefined` into the address.
 */
function getPostalCodeType(candidate: AddressCandidate): string {
  const attributes = attrsOf(candidate)
  const postal = str(attributes.Postal)

  if (str(attributes.Country) === "USA") {
    return postal
  }

  const postalExt = str(attributes.PostalExt)
  return [postal, postalExt].filter(Boolean).join(" ")
}

export function convertToMblsAddress(candidate: AddressCandidate): MblsAddress {
  const attributes = attrsOf(candidate)

  const addNum = str(attributes.AddNum)
  const longLabel = str(attributes.LongLabel)
  const shortLabel = str(attributes.ShortLabel)

  // `"x".replace("", "")` is a no-op, so an absent street number simply leaves
  // the label intact instead of mangling it.
  const withoutAddNum = (label: string) =>
    addNum ? label.replace(addNum, "").trim() : label.trim()

  const country = resolveCandidateCountry(candidate)

  const x = num(attributes.X)

  const place: MblsAddress = {
    id: Math.random(),
    address_components: [
      {
        long_name: addNum,
        short_name: addNum,
        types: ["street_number"],
      },
      {
        long_name: withoutAddNum(longLabel),
        short_name: withoutAddNum(shortLabel),
        types: ["route"],
      },
      {
        long_name: str(attributes.City),
        short_name: str(attributes.City),
        types: ["locality", "political"],
      },
      {
        long_name: str(attributes.Region),
        short_name: str(attributes.RegionAbbr),
        types: ["administrative_area_level_1", "political"],
      },
      {
        long_name: country?.long || "",
        short_name: country?.short || "",
        types: ["country", "political"],
      },
      {
        long_name: getPostalCodeType(candidate),
        short_name: getPostalCodeType(candidate),
        types: ["postal_code"],
      },
      {
        long_name: str(attributes.District),
        short_name: str(attributes.District),
        types: ["district"],
      },
    ],
    formatted_address: longLabel,
    geometry: {
      location: {
        lat: num(attributes.Y),
        lng: x,
      },
    },
  }

  // WV1-59 Address route calculations problems - Workaround to ESRI bug
  if (x > 85 || x < -85) {
    place.geometry.location.lat = num(attributes.DisplayY)
    place.geometry.location.lng = num(attributes.DisplayX)
  }

  return place
}

// Example usage:
// const candidate: AddressCandidate = {
//   address: "1200 Rue Lincoln, Sherbrooke, QC, J1H 2H9, CAN",
//   location: { x: -71.8962095, y: 45.4003028 },
//   score: 100,
//   attributes: {
//     AddNum: "1200",
//     LongLabel: "1200 Rue Lincoln, Sherbrooke, QC, J1H 2H9, CAN",
//     ShortLabel: "1200 Rue Lincoln",
//     City: "Sherbrooke",
//     Region: "Quebec",
//     RegionAbbr: "QC",
//     Country: "CAN",
//     Postal: "J1H 2H9",
//     PostalExt: "",
//     District: "",
//     X: -71.8962095,
//     Y: 45.4003028,
//     DisplayX: -71.8962095,
//     DisplayY: 45.4003028,
//   },
// }

// // const place = convertToMblsAddress(candidate)
// // console.log(place)
